import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listData, createData, updateData, deleteData, bulkImport, parseCSV } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, PencilSimple, Trash, Sparkle, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/api";

export default function DataTablePage({ collection, columns, title, subtitle, eyebrow, emptyDefault, emptyMessage }) {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const fileRef = useRef(null);

  const key = ["data", collection, activeId];
  const { data: rows = [], isLoading } = useQuery({
    queryKey: key, queryFn: () => listData(collection, activeId), enabled: !!activeId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["dashboard", activeId] });
  };

  const createM = useMutation({
    mutationFn: (payload) => createData(collection, { ...payload, client_id: activeId }),
    onSuccess: () => { invalidate(); toast.success("Added"); },
  });
  const updateM = useMutation({
    mutationFn: ({ id, payload }) => updateData(collection, id, payload),
    onSuccess: () => { invalidate(); toast.success("Updated"); },
  });
  const deleteM = useMutation({
    mutationFn: (id) => deleteData(collection, id),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
  });

  const numericKeys = columns.filter((c) => c.type === "number" || c.type === "money").map((c) => c.key);
  const boolKeys = columns.filter((c) => c.type === "bool").map((c) => c.key);

  const importM = useMutation({
    mutationFn: (rows) => bulkImport(collection, activeId, rows),
    onSuccess: (res) => { invalidate(); toast.success(`Imported ${res.inserted} rows`); },
    onError: () => toast.error("Import failed — check your CSV columns"),
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseCSV(text).map((row) => {
      const out = { ...row };
      numericKeys.forEach((k) => { if (out[k] !== undefined && out[k] !== "") out[k] = Number(out[k]) || 0; });
      boolKeys.forEach((k) => { if (out[k] !== undefined) out[k] = ["true", "1", "yes"].includes(String(out[k]).toLowerCase()); });
      return out;
    });
    if (!parsed.length) return toast.error("No rows found in CSV");
    importM.mutate(parsed);
    e.target.value = "";
  };

  const downloadTemplate = () => {
    const header = columns.map((c) => c.key).join(",");
    const blob = new Blob([header + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${collection}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyDefault || {});
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...row });
    setOpen(true);
  };
  const save = () => {
    if (editing) updateM.mutate({ id: editing.id, payload: form });
    else createM.mutate(form);
    setOpen(false);
  };

  const renderCell = (col, row) => {
    const v = row[col.key];
    if (col.type === "badge") return <StatusBadge value={v} />;
    if (col.type === "money") return fmtMoney(v);
    if (col.type === "number") return Number(v || 0).toLocaleString();
    if (col.type === "bool") return v ? <span className="text-emerald-400">Yes</span> : <span className="text-[#7a7a7a]">No</span>;
    return <span className="text-[#cfcfcf]">{v}</span>;
  };

  return (
    <PageContainer>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle}>
        <input ref={fileRef} type="file" accept=".csv" onChange={onFile} className="hidden" data-testid={`import-input-${collection}`} />
        <Button data-testid={`template-${collection}-btn`} onClick={downloadTemplate} variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-white">
          <DownloadSimple size={16} className="mr-1.5" /> Template
        </Button>
        <Button data-testid={`import-${collection}-btn`} onClick={() => fileRef.current?.click()} variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-white">
          <UploadSimple size={16} className="mr-1.5" /> Import CSV
        </Button>
        <Button data-testid={`add-${collection}-btn`} onClick={openNew} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
          <Plus size={16} className="mr-1.5" /> Add
        </Button>
      </PageHeader>

      <div className="card-matte overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                {columns.map((c) => (
                  <TableHead key={c.key} className="label-caps text-[#7a7a7a]">{c.label}</TableHead>
                ))}
                <TableHead className="text-right label-caps text-[#7a7a7a]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-[#7a7a7a]">Loading…</TableCell></TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-[#7a7a7a]" data-testid="table-empty-state">{emptyMessage || "No records yet. Click Add to create one."}</TableCell></TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.id} className="border-white/8 hover:bg-white/[0.03]" data-testid={`row-${collection}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className="py-3 max-w-[280px] truncate">{renderCell(c, row)}</TableCell>
                  ))}
                  <TableCell className="text-right">
                    <button data-testid={`edit-${collection}-btn`} onClick={() => openEdit(row)} className="text-[#9a9a9a] hover:text-white p-1.5"><PencilSimple size={16} /></button>
                    <button data-testid={`delete-${collection}-btn`} onClick={() => deleteM.mutate(row.id)} className="text-[#9a9a9a] hover:text-red-400 p-1.5"><Trash size={16} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Edit" : "Add"} {title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {columns.filter((c) => c.type !== "badge" || c.options).map((c) => (
              <div key={c.key}>
                <Label className="label-caps">{c.label}</Label>
                {c.type === "textarea" ? (
                  <Textarea value={form[c.key] || ""} onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
                    className="bg-[#10101A] border-white/10 mt-1" rows={4} data-testid={`field-${c.key}`} />
                ) : c.type === "select" || (c.type === "badge" && c.options) ? (
                  <Select value={form[c.key] || ""} onValueChange={(val) => setForm({ ...form, [c.key]: val })}>
                    <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid={`field-${c.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#14141F] border-white/10 text-white">
                      {c.options.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : c.type === "bool" ? (
                  <Select value={String(form[c.key] ?? false)} onValueChange={(val) => setForm({ ...form, [c.key]: val === "true" })}>
                    <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid={`field-${c.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14141F] border-white/10 text-white">
                      <SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input type={c.type === "number" || c.type === "money" ? "number" : "text"}
                    value={form[c.key] ?? ""} onChange={(e) => setForm({ ...form, [c.key]: c.type === "number" || c.type === "money" ? Number(e.target.value) : e.target.value })}
                    className="bg-[#10101A] border-white/10 mt-1" data-testid={`field-${c.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="save-row-btn" onClick={save} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
